/**
 * After a deploy, a tab opened on the previous build asks for code split into files that no longer exist (their names
 * carry a hash: CommandPalette-<hash>.js → 404), so opening search, a modal or a tab left a white page (2026-10-31).
 * When such a file fails to load, the page reloads once to get the new build; if it already did that a moment ago, a
 * small bar says Flow was updated and offers Reload instead of reloading in a loop.
 *
 * Before that bar, it sends FLOW_UPDATED_EVENT on window (cancelable) and notes it (flowUpdateSeen()): the app's own
 * "Flow was updated" banner (components/sandbox/SandboxWaking.jsx, via hooks/useSandboxWake.js) cancels the event
 * when it shows, and this plain bar shows only when nothing did (e.g. React itself failed to load).
 */
const KEY = 'flow.staleBuildReloadAt';
const WINDOW_MS = 30_000;
const CHUNK_ERROR = /dynamically imported module|Importing a module script failed|error loading dynamically imported|Unable to preload CSS/i;

export const FLOW_UPDATED_EVENT = 'flow:new-version';
let updateSeen = false;

/** Whether this tab found out that a new Flow version is out (a chunk of its build is gone). */
export const flowUpdateSeen = () => updateSeen;

/** Tests only. */
export function resetFlowUpdateSeen() { updateSeen = false; }

/** Tells the app a new version is out; true when something on the page took it (and shows it). */
function announceUpdate(win) {
  updateSeen = true;
  if (typeof win.CustomEvent !== 'function') { return false; }
  const event = new win.CustomEvent(FLOW_UPDATED_EVENT, { cancelable: true });
  win.dispatchEvent(event);
  return event.defaultPrevented;
}

export const isChunkLoadError = (error) => CHUNK_ERROR.test(String(error?.message || error || ''));

function showUpdatedBar(doc) {
  if (doc.getElementById('flow-updated-bar')) { return; }
  const bar = doc.createElement('div');
  bar.id = 'flow-updated-bar';
  bar.setAttribute('role', 'alert');
  bar.style.cssText = 'position:fixed;left:50%;bottom:calc(16px + env(safe-area-inset-bottom, 0px));transform:translateX(-50%);z-index:30000;'
    + 'display:flex;gap:12px;align-items:center;padding:10px 14px;border-radius:10px;background:#1f2937;color:#fff;font:14px system-ui,sans-serif;box-shadow:0 8px 24px rgba(0,0,0,.25)';
  bar.textContent = 'Flow was updated. ';
  const button = doc.createElement('button');
  button.type = 'button';
  button.textContent = 'Reload';
  button.style.cssText = 'border:0;border-radius:6px;padding:4px 10px;background:#fff;color:#1f2937;font:inherit;cursor:pointer';
  button.addEventListener('click', () => doc.defaultView.location.reload());
  bar.appendChild(button);
  doc.body.appendChild(bar);
}

/** Starts watching; returns a function that stops. Arguments are for tests. */
export function watchStaleBuild({ win = window, now = Date.now, reload = () => win.location.reload() } = {}) {
  const recover = () => {
    let last = 0;
    try { last = Number(win.sessionStorage.getItem(KEY)) || 0; } catch { last = 0; }
    if (now() - last < WINDOW_MS) {
      if (!announceUpdate(win)) { showUpdatedBar(win.document); }
      return;
    }
    try { win.sessionStorage.setItem(KEY, String(now())); } catch { /* private mode: reload anyway */ }
    reload();
  };
  const onPreload = (event) => { event.preventDefault?.(); recover(); };
  const onRejection = (event) => { if (isChunkLoadError(event?.reason)) { event.preventDefault?.(); recover(); } };
  win.addEventListener('vite:preloadError', onPreload);
  win.addEventListener('unhandledrejection', onRejection);
  return () => {
    win.removeEventListener('vite:preloadError', onPreload);
    win.removeEventListener('unhandledrejection', onRejection);
  };
}
